
import { useState } from 'react';
import { History, X, ExternalLink, Clock, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { WorkflowRunSummary } from '@/types/workflow-replay';
import { getStatusVariant, formatDuration } from '@/types/workflow-replay';
import { RunSelectorDrawer } from './RunSelectorDrawer';

interface WorkflowReplayToolbarProps {
    isReplayMode: boolean;
    availableRuns: WorkflowRunSummary[];
    selectedRunId: string | null;
    selectedRun: WorkflowRunSummary | null;
    isLoading: boolean;
    grafanaUrl: string | null;
    onExitReplay: () => void;
    onSelectRun: (runId: string) => void;
    onRefresh: () => void;
}

/**
 * Toolbar component for workflow replay mode.
 * 
 * Displays run selector button, status, duration, and Grafana link.
 * Opens a bottom drawer for run selection with full details.
 */
export function WorkflowReplayToolbar({
    isReplayMode,
    availableRuns,
    selectedRunId,
    selectedRun,
    isLoading,
    grafanaUrl,
    onExitReplay,
    onSelectRun,
    onRefresh,
}: WorkflowReplayToolbarProps) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    if (!isReplayMode) {
        return null;
    }

    return (
        <>
            <div className="flex items-center gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-1.5 shadow-sm min-w-[520px]">
                {/* Replay mode indicator */}
                <div className="flex items-center gap-1.5 text-indigo-700">
                    <History className="h-4 w-4" />
                    <span className="text-xs font-medium">Replay</span>
                </div>

                <div className="h-4 w-px bg-indigo-200" />

                {/* Run selector button - opens drawer */}
                <button
                    onClick={() => setIsDrawerOpen(true)}
                    disabled={isLoading}
                    className={cn(
                        'flex items-center gap-2 px-3 py-1 rounded-md border',
                        'bg-white text-xs transition-colors',
                        'hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
                        selectedRun
                            ? 'border-indigo-300'
                            : 'border-slate-200 text-slate-500'
                    )}
                >
                    {selectedRun ? (
                        <>
                            <Badge
                                variant={getStatusVariant(selectedRun.status)}
                                className="h-4 px-1 text-[10px]"
                            >
                                {selectedRun.status}
                            </Badge>
                            <span className="text-slate-600">
                                {formatRunTimestamp(selectedRun.created)}
                            </span>
                        </>
                    ) : (
                        <span>Select a run to replay...</span>
                    )}
                    <ChevronDown className="h-3 w-3 text-slate-400" />
                </button>

                {/* Selected run info */}
                {selectedRun && (
                    <>
                        <div className="h-4 w-px bg-indigo-200" />
                        
                        {/* Status badge */}
                        <Badge
                            variant={getStatusVariant(selectedRun.status)}
                            className="h-5"
                        >
                            {selectedRun.status}
                        </Badge>

                        {/* Duration */}
                        {selectedRun.duration_ms && (
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                                <Clock className="h-3 w-3" />
                                {formatDuration(selectedRun.duration_ms)}
                            </div>
                        )}

                        {/* Trace indicator */}
                        {selectedRun.has_trace && (
                            <Badge variant="outline" className="h-5 text-[10px] border-green-300 text-green-700 bg-green-50">
                                OTEL
                            </Badge>
                        )}
                    </>
                )}

                {/* Grafana link */}
                {grafanaUrl && (
                    <a
                        href={grafanaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                        <ExternalLink className="h-3 w-3" />
                        Grafana
                    </a>
                )}

                {/* Loading indicator */}
                {isLoading && (
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                )}

                <div className="flex-1" />

                {/* Exit replay mode */}
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-indigo-600 hover:bg-indigo-100"
                    onClick={onExitReplay}
                >
                    <X className="mr-1 h-3 w-3" />
                    Exit Replay
                </Button>
            </div>

            {/* Run Selector Drawer */}
            <RunSelectorDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                runs={availableRuns}
                selectedRunId={selectedRunId}
                isLoading={isLoading}
                onSelectRun={onSelectRun}
                onRefresh={onRefresh}
            />
        </>
    );
}

/**
 * Format run timestamp for display in selector button.
 */
function formatRunTimestamp(timestamp: string): string {
    try {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) {
            return 'just now';
        }
        if (diffMins < 60) {
            return `${diffMins}m ago`;
        }
        if (diffHours < 24) {
            return `${diffHours}h ago`;
        }
        if (diffDays < 7) {
            return `${diffDays}d ago`;
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return timestamp;
    }
}
